import { Link } from "react-router-dom";
import { m } from "framer-motion";
import { services } from "@/lib/services-data";
import { ArrowRight, Paintbrush, PaintBucket, Home, Building2, Wrench } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useQuoteForm } from "@/components/QuoteFormContext";

/** Matches the order of `services` — interior, exterior, residential, commercial, drywall. */
const serviceIcons = [Paintbrush, PaintBucket, Home, Building2, Wrench];

const ServicesSection = () => {
  const { openQuoteForm } = useQuoteForm();

  return (
    <section id="services" className="relative -mt-px section-padding section-gradient-soft">
      <div className="container-max">
        <div className="text-center mb-14 md:mb-16">
          <m.span
            initial={{ opacity: 0, y: 10 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="inline-block text-primary text-sm font-bold uppercase tracking-widest mb-3"
          >
            What We Offer
          </m.span>
          <m.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="font-heading text-4xl md:text-5xl lg:text-6xl text-foreground mb-4 tracking-tight font-bold"
          >
            Our <span className="text-gradient-primary">Services</span>
          </m.h2>
          <m.p
            initial={{ opacity: 0, y: 15 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 }}
            className="text-muted-foreground max-w-2xl mx-auto text-lg"
          >
            Interior, exterior, residential, commercial, and drywall painting — done with proper
            prep and a finish that holds up across Middle Tennessee.
          </m.p>
        </div>

        {/* 2-col grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-7">
          {services.map((service, i) => {
            const Icon = serviceIcons[i] || Home;
            // With an odd number of services the last card is alone on its row — center it.
            const isOrphan = i === services.length - 1 && services.length % 2 === 1;

            return (
              <m.div
                key={service.slug}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.08, duration: 0.5 }}
                className={`w-full flex ${
                  isOrphan
                    ? "md:col-span-2 md:mx-auto md:w-[calc(50%-0.75rem)] lg:w-[calc(50%-0.875rem)]"
                    : ""
                }`}
              >
                <div
                  className="group relative rounded-2xl w-full flex flex-col card-hover-lift bg-card border border-border shadow-sm hover:shadow-2xl overflow-hidden isolate"
                >
                  <Link to={`/services/${service.slug}`} className="relative aspect-[4/3] w-full block overflow-hidden">
                    <img
                      src={service.image}
                      srcSet={service.imageSrcSet}
                      sizes="(min-width: 1312px) 612px, (min-width: 768px) calc(50vw - 28px), 100vw"
                      alt={service.title}
                      className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                      loading="lazy"
                    />
                    <div className="absolute inset-0 overlay-gradient" />

                    <div className="absolute top-4 left-4 z-10">
                      <div className="h-12 w-12 rounded-xl bg-primary/95 flex items-center justify-center backdrop-blur-sm shadow-lg">
                        <Icon className="h-6 w-6 text-primary-foreground" />
                      </div>
                    </div>

                    <div className="absolute top-4 right-4 z-10">
                      <span className="inline-block bg-secondary-dark text-secondary-foreground text-xs font-bold px-3 py-1.5 rounded-full uppercase tracking-wider shadow-md">
                        {service.price}
                      </span>
                    </div>

                    <div className="relative z-10 flex flex-col justify-end h-full p-6">
                      <h3 className="font-heading text-2xl text-white mb-1 tracking-tight font-bold drop-shadow-md">
                        {service.title}
                      </h3>
                      <p className="text-white/85 text-sm">{service.subtitle}</p>
                    </div>
                  </Link>

                  <div className="mt-auto p-5 bg-card border-t border-border">
                    <Link
                      to={`/services/${service.slug}`}
                      className="inline-flex items-center gap-2 text-primary font-semibold text-sm hover:gap-3 transition-all"
                    >
                      {`Explore ${service.title}`} <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                    </Link>
                  </div>
                </div>
              </m.div>
            );
          })}
        </div>

        <div className="flex flex-col sm:flex-row justify-center gap-3 mt-12">
          <Button size="lg" onClick={openQuoteForm} className="gap-2 rounded-xl font-semibold">
            Get Free Estimate <ArrowRight className="h-5 w-5" />
          </Button>
          <Link to="/services">
            <Button size="lg" variant="outline" className="gap-2 rounded-xl font-semibold w-full">
              View All Services
            </Button>
          </Link>
        </div>
      </div>
    </section>
  );
};

export default ServicesSection;
